// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Static, unauthenticated legal pages (Terms of Service, Privacy Policy) served at
// /terms and /privacy. The homeserver's SPA catch-all (web/ui_embed.go) returns
// index.html for any path, and App.tsx routes these by pathname. The copy below is a
// PLACEHOLDER — replace it with text reviewed by counsel before production use.

import { Button, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import type { ReactNode } from "react";
import { APP_VERSION } from "../version";

const { Title, Paragraph } = Typography;

// Operator-facing constants — edit these (or wire to config) for your deployment.
const COMPANY = "Dobrev IT Ltd";
const CONTACT_EMAIL = "contact@zalmoxis.chat";
const LAST_UPDATED = "29 June 2026";

function LegalPage({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
  return (
    <div className="legal-page">
      <div className="legal-content">
        <Button type="text" href="/" icon={<ArrowLeftOutlined />} className="legal-back">
          Back to Zalmoxis
        </Button>

        <div className="legal-placeholder">
          ⚠️ <strong>Placeholder document.</strong> This is sample copy, not legal advice. Replace it
          with terms reviewed by qualified counsel before relying on it.
        </div>

        <Title level={1} className="legal-title">
          {title}
        </Title>
        <Paragraph type="secondary">
          Last updated {LAST_UPDATED} · Zalmoxis {APP_VERSION}
        </Paragraph>
        <Paragraph>{intro}</Paragraph>

        {children}

        <Paragraph type="secondary">
          Questions? Contact <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </Paragraph>

        <footer className="legal-footer">
          <span>
            <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a> · Self-hostable · AGPL-3.0
          </span>
          <span>© 2026 {COMPANY}</span>
        </footer>
      </div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <>
      <Title level={3} className="legal-heading">
        {heading}
      </Title>
      <Paragraph>{children}</Paragraph>
    </>
  );
}

export function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      intro="These terms govern your use of this Zalmoxis homeserver. By creating an account or otherwise using the service you agree to them. Each Zalmoxis homeserver is operated independently; the operator of this server is responsible for these terms."
    >
      <Section heading="1. The service">
        Zalmoxis is a decentralized, end-to-end-encrypted, federated messaging service. Messages are
        encrypted on your device; the homeserver stores and relays only ciphertext and the minimum
        metadata required to route it.
      </Section>
      <Section heading="2. Your account & devices">
        You are responsible for safeguarding your account credentials and device keys. Because content
        is end-to-end encrypted, the operator cannot recover your messages if you lose your keys and
        have not configured key backup or recovery codes.
      </Section>
      <Section heading="3. Acceptable use">
        You agree not to use the service to break the law, infringe others’ rights, distribute malware,
        or send unsolicited bulk messages. Moderators and channel operators may remove content or members
        from the rooms they administer.
      </Section>
      <Section heading="4. Federation">
        This server may federate with other homeservers so you can communicate with their users. Those
        servers are operated by third parties under their own terms; the operator of this server is not
        responsible for them.
      </Section>
      <Section heading="5. Self-hosting & open source">
        Zalmoxis is free software licensed under AGPL-3.0-or-later. You may run your own homeserver. These
        terms apply to your use of THIS operator’s hosted instance, not to software you self-host.
      </Section>
      <Section heading="6. No warranty">
        The service is provided “as is”, without warranties of any kind, to the fullest extent permitted
        by law. The operator is not liable for indirect or consequential damages arising from your use.
      </Section>
      <Section heading="7. Changes">
        These terms may be updated from time to time. Continued use after a change constitutes acceptance
        of the revised terms.
      </Section>
    </LegalPage>
  );
}

export function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro="This policy explains what this Zalmoxis homeserver does and does not collect. Zalmoxis is designed to minimize data: message content is end-to-end encrypted and never visible to the server."
    >
      <Section heading="1. What the server can see">
        The homeserver stores only ciphertext for your messages and media, plus routing metadata (account
        and device identifiers, room membership, timestamps, and the signed event graph). It cannot read
        your message contents, attachments, or call media.
      </Section>
      <Section heading="2. What the server cannot see">
        Message bodies, attachments, and call audio/video are encrypted end-to-end on your devices.
        Encryption keys never leave your devices unencrypted, and the operator has no access to them.
      </Section>
      <Section heading="3. Push notifications">
        If you enable push notifications, the server sends content-free wake-ups (room and sender
        identifiers only) through your chosen push provider. Message text is never included in a push.
      </Section>
      <Section heading="4. Logs & metadata">
        The operator may keep operational logs (e.g. IP addresses, request times) for security and
        reliability. Privacy-hardening options can reduce metadata retention; ask the operator about this
        server’s configuration.
      </Section>
      <Section heading="5. Federation">
        When you communicate with users on other homeservers, the metadata needed to route your encrypted
        events is shared with those servers. Their handling of data is governed by their own policies.
      </Section>
      <Section heading="6. Your choices">
        You can delete messages (redaction), leave rooms, and request account deactivation. Because of
        federation and end-to-end encryption, copies already delivered to other devices/servers may persist
        beyond your control.
      </Section>
      <Section heading="7. Changes">
        This policy may be updated from time to time; material changes will be reflected by the “last
        updated” date above.
      </Section>
    </LegalPage>
  );
}
